import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Users, RefreshCw, Circle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleColors = {
  administrator: 'bg-amber-500',
  manager: 'bg-blue-500',
  viewer: 'bg-green-500'
};

const roleLabels = {
  administrator: '👑 Admin',
  manager: '⚡ Manager',
  viewer: '👁️ Viewer'
};

export default function OnlineUsersDialog({ open, onClose }) {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  const fetchOnlineUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/online`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnlineUsers(response.data.online_users || []);
    } catch (error) {
      console.error('Error fetching online users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchOnlineUsers();
      // Refresh every 30 seconds
      intervalRef.current = setInterval(fetchOnlineUsers, 30000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [open]);

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeSince = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffSecs = Math.floor(diffMs / 1000);
    if (diffSecs < 60) return 'Just now';
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins === 1) return '1 min ago';
    return `${diffMins} mins ago`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="online-users-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0f172a] flex items-center gap-2">
            <Users className="w-6 h-6 text-green-500" />
            Online Users
            <span className="ml-2 px-2 py-1 text-sm bg-green-100 text-green-700 rounded-full">
              {onlineUsers.length} online
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-end mb-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchOnlineUsers}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {loading && onlineUsers.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : onlineUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-slate-500">
              <Users className="w-8 h-8 mb-2" />
              <p>No users online</p>
            </div>
          ) : (
            onlineUsers.map((user) => (
              <div
                key={user.user_id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                data-testid={`online-user-${user.user_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className={`w-10 h-10 rounded-full ${roleColors[user.user_role]} flex items-center justify-center text-white font-semibold`}>
                      {user.user_name?.charAt(0).toUpperCase()}
                    </div>
                    <Circle 
                      className="absolute -bottom-0.5 -right-0.5 w-4 h-4 text-green-500 fill-green-500" 
                    />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{user.user_name}</p>
                    <p className="text-xs text-slate-500">{user.user_email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs px-2 py-1 bg-slate-200 text-slate-600 rounded-full capitalize">
                    {roleLabels[user.user_role]}
                  </span>
                  <p className="text-xs text-slate-400 mt-1">
                    {getTimeSince(user.last_activity)}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={onClose} data-testid="close-online-users">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
